import { createContext, useContext, useEffect, useRef, type MutableRefObject, type ReactNode, type Ref } from 'react';
import { BackIcon, CloseIcon } from '../Icons';
import { useOverlay } from '../Overlay';

/**
 * The phone's Back inside a record panel (mobile review, 19 Oct: it left Contact and lost what was typed). The
 * screen that shows the panels keeps one history step while one is open and provides this; the open panel puts
 * its own back in it, so Back steps back one level, as the arrow does: an invoice to the list, a form to the
 * booking, the panel closed.
 */
export interface RecordStep {
  back: () => void;
  /** The panel stays open after `back`: it steps inside, or asks before discarding. */
  stays: () => boolean;
}
export const RecordBack = createContext<MutableRefObject<RecordStep | null> | null>(null);

/**
 * A Sentinel record over the chat (Gary, 18 Oct: Customer, Bookings and History in three buttons): full screen
 * on a phone with a back arrow, a centred window on a computer. Escape or the cross closes it. With `unsaved`,
 * leaving by any way asks first, so typing is never lost to a stray tap or swipe.
 */
export function RecordPanel({ title, sub, onClose, onBack, children, footer, unsaved = false, bodyRef }: {
  title: string; sub?: string; onClose: () => void; onBack?: () => void; children: ReactNode; footer?: ReactNode;
  unsaved?: boolean; bodyRef?: Ref<HTMLDivElement>;
}) {
  const { openSheet, closeSheet, open: sheetOpen } = useOverlay();
  const leave = (go: () => void) => () => {
    if (!unsaved) { go(); return; }
    openSheet(
      <>
        <h2 className="chats-sheet-title">Discard changes?</h2>
        <p className="chats-sheet-hint">What you typed here has not been saved.</p>
        <div className="chats-pair">
          <button type="button" className="chats-btn chats-btn--ghost" onClick={closeSheet}>Keep editing</button>
          <button type="button" className="chats-btn chats-btn--danger" onClick={() => { closeSheet(); go(); }}>Discard</button>
        </div>
      </>,
    );
  };
  const back = leave(onBack ?? onClose);
  const close = leave(onClose);

  // The latest back, for the phone's Back.
  const step = useContext(RecordBack);
  const backRef = useRef(back);
  backRef.current = back;
  const staysRef = useRef(false);
  staysRef.current = !!onBack || unsaved;
  useEffect(() => {
    if (!step) return;
    const mine: RecordStep = { back: () => backRef.current(), stays: () => staysRef.current };
    step.current = mine;
    return () => { if (step.current === mine) step.current = null; };
  }, [step]);

  const sheetOpenRef = useRef(sheetOpen);
  sheetOpenRef.current = sheetOpen;
  useEffect(() => {
    // Escape with the discard question open closes the question only.
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape' && !sheetOpenRef.current) backRef.current(); };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, []);
  return (
    <div className="chats-rec-overlay" onClick={close}>
      <div className="chats-rec-panel" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <header className="chats-rec-head">
          <button type="button" className="chats-icon-btn" aria-label={onBack ? 'Back' : 'Close'} onClick={back}>{onBack ? <BackIcon /> : <CloseIcon />}</button>
          <span className="chats-rec-titles">
            <b>{title}</b>
            {sub && <small>{sub}</small>}
          </span>
          {onBack && <button type="button" className="chats-icon-btn chats-rec-close" aria-label="Close" onClick={close}><CloseIcon /></button>}
        </header>
        <div className="chats-rec-body" ref={bodyRef}>{children}</div>
        {footer && <footer className="chats-rec-foot">{footer}</footer>}
      </div>
    </div>
  );
}
